/** Which lesson teaches which objectives (STORY-050): lessons as rows of objective chips,
 * a picker per lesson, and one-tap automatic spreading in tree order. */
import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../api/client";
import { autoAllocate, dismissWarning, restoreWarning, setLessonObjectives, type Allocation, type Coverage, type LO } from "./api";
import { Button, useAskText, useConfirm } from "../ui";

export function AllocationPanel({
  courseId,
  allocation,
  los,
  coverage = null,
  onChanged,
  onNext,
}: {
  courseId: string;
  allocation: Allocation | null;
  los: LO[];
  coverage?: Coverage | null;
  onChanged: () => void;
  onNext: () => void;
}) {
  const { t, i18n } = useTranslation();
  const byId = new Map(los.map((lo) => [lo.lo_id, lo]));
  const confirm = useConfirm();
  const askText = useAskText();
  const approved = los.filter((lo) => lo.review_status === "approved");
  const set = useMutation({
    mutationFn: ({ lessonId, ids }: { lessonId: string; ids: string[] }) => setLessonObjectives(lessonId, ids),
    onSuccess: onChanged,
  });
  const auto = useMutation({
    mutationFn: (replace: boolean) => autoAllocate(courseId, replace),
    onSuccess: onChanged,
    onError: async (e) => {
      if (e instanceof ApiError && e.code === "allocation_exists") {
        const n = (e.body as { allocated?: number })?.allocated ?? 0;
        if (await confirm({ title: t("alloc.autoTitle", { n }), body: t("alloc.autoConfirm"), confirmLabel: t("alloc.auto"), destructive: true })) auto.mutate(true);
      }
    },
  });
  const dismiss = useMutation({
    mutationFn: ({ key, reason }: { key: string; reason: string }) => dismissWarning(courseId, key, reason),
    onSuccess: onChanged,
  });
  const restore = useMutation({ mutationFn: (key: string) => restoreWarning(courseId, key), onSuccess: onChanged });
  const lessonNo = (id: string | null) => (allocation ? allocation.lessons.findIndex((l) => l.lesson_id === id) + 1 : 0);
  const warningText = (w: Coverage["warnings"][number]) =>
    t(`alloc.warn.${w.kind}`, { code: w.lo_code ?? "", pre: w.related_lo_code ?? "", n: lessonNo(w.lesson_id) });
  const fmt = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString(i18n.language, { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
  const card = "flex flex-col gap-3 rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm";

  if (!allocation || allocation.lessons.length === 0) {
    return (
      <section className={card}>
        <h2 className="display text-xl">{t("alloc.title")}</h2>
        <p className="text-sm text-[color:var(--ink-2)]">{t("alloc.empty")}</p>
      </section>
    );
  }
  const unallocated = allocation.unallocated.map((id) => byId.get(id)).filter((x): x is LO => !!x);
  return (
    <section className={card}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="display text-xl">{t("alloc.title")}</h2>
          <p className="measure mt-1 text-sm text-[color:var(--ink-2)]">{t("alloc.body")}</p>
        </div>
        <Button variant="secondary" loading={auto.isPending} disabled={approved.length === 0} onClick={() => auto.mutate(false)}>{t("alloc.auto")}</Button>
      </div>
      {approved.length === 0 && <p className="text-sm">{t("alloc.noObjectives")}</p>}
      {unallocated.length > 0 && (
        <div className="rounded-[var(--radius-control)] border border-dashed p-3 text-sm">
          <p className="mb-1 font-medium">{t("alloc.unallocated")}</p>
          <p className="flex flex-wrap gap-1">
            {unallocated.map((lo) => (
              <span key={lo.lo_id} className="rounded-full border px-2 py-0.5 text-xs"><span className="mr-1 font-mono">{lo.code}</span>{lo.text.length > 40 ? `${lo.text.slice(0, 40)}…` : lo.text}</span>
            ))}
          </p>
        </div>
      )}
      <ol className="grid-fit" style={{ "--fit-min": "26rem" } as CSSProperties}>
        {allocation.lessons.map((lesson, i) => {
          const chosen = lesson.lo_ids.map((id) => byId.get(id)).filter((x): x is LO => !!x);
          const candidates = approved.filter((lo) => !lesson.lo_ids.includes(lo.lo_id));
          return (
            <li key={lesson.lesson_id} className="rounded-[var(--radius-control)] bg-[color:var(--surface-2)] p-3">
              <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
                <span className="bignum">{i + 1}</span>
                <span>{fmt(lesson.date)}</span>
                <span className="text-[color:var(--ink-2)]">{t(`schedule.kinds.${lesson.kind}`)}</span>
                {lesson.title && <span className="text-[color:var(--ink-2)]">· {lesson.title}</span>}
                {lesson.lo_ids.length > 0 && (
                  <Link to="/t/c/$courseId/lessons/$lessonId" params={{ courseId, lessonId: lesson.lesson_id }} className="ml-auto underline">{t("alloc.planLesson")}</Link>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-1">
                {chosen.map((lo) => (
                  <span key={lo.lo_id} className="inline-flex items-center gap-1 rounded-full bg-[color:var(--surface)] px-2 py-1 text-xs shadow-sm">
                    <span className="font-mono">{lo.code}</span>
                    <span className="max-w-48 truncate">{lo.text}</span>
                    <button type="button" aria-label={t("alloc.remove")} className="inline-flex min-h-6 min-w-6 items-center justify-center" onClick={() => set.mutate({ lessonId: lesson.lesson_id, ids: lesson.lo_ids.filter((x) => x !== lo.lo_id) })}>×</button>
                  </span>
                ))}
                <select
                  className="field field-sm min-w-0 max-w-full"
                  aria-label={t("alloc.add")}
                  value=""
                  disabled={candidates.length === 0}
                  onChange={(e) => {
                    if (e.target.value) set.mutate({ lessonId: lesson.lesson_id, ids: [...lesson.lo_ids, e.target.value] });
                  }}
                >
                  <option value="">{t("alloc.add")}</option>
                  {candidates.map((lo) => (
                    <option key={lo.lo_id} value={lo.lo_id}>{lo.code} · {lo.text.length > 50 ? `${lo.text.slice(0, 50)}…` : lo.text}</option>
                  ))}
                </select>
              </div>
            </li>
          );
        })}
      </ol>
      {coverage && coverage.warnings.length > 0 && (
        <div className="rounded-[var(--radius-control)] border border-dashed p-3 text-sm">
          <p className="mb-1 font-medium">{t("alloc.gaps", { count: coverage.open })}</p>
          <ul className="flex flex-col gap-1">
            {coverage.warnings.map((w) => (
              <li key={w.key} className={`flex flex-wrap items-center gap-2 ${w.dismissed ? "text-[color:var(--ink-2)] line-through" : ""}`}>
                <span className="no-underline">{warningText(w)}</span>
                {w.dismissed ? (
                  <>
                    {w.reason && <span className="text-xs no-underline">({w.reason})</span>}
                    <button type="button" className="text-xs underline" onClick={() => restore.mutate(w.key)}>{t("alloc.restore")}</button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="text-xs underline"
                    onClick={async () => {
                      const reason = await askText({ title: t("alloc.dismissTitle"), body: warningText(w), label: t("alloc.dismissWhy"), confirmLabel: t("alloc.dismiss"), multiline: true });
                      if (reason !== null) dismiss.mutate({ key: w.key, reason });
                    }}
                  >
                    {t("alloc.dismiss")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {allocation.unallocated.length === 0 && approved.length > 0 && (
        <p className="text-sm">
          <button type="button" className="underline" onClick={onNext}>{t("alloc.next")}</button>
        </p>
      )}
    </section>
  );
}
